import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { format, parseISO } from 'date-fns';
import { it } from 'date-fns/locale';
import { toast } from 'sonner';
import {
  RefreshCw, LogOut, Plus, Trash2, Clock, ChevronRight,
} from 'lucide-react';

import { useAuth } from '@/contexts/AuthContext';
import { useOperationsProjects } from '@/hooks/useOperationsProjects';
import { useOperationsTasks } from '@/hooks/useOperationsTasks';
import { useOperationsShoots } from '@/hooks/useOperationsShoots';
import { API } from '@/config/api';

import ScheduleCalendar from '@/components/ScheduleCalendar';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';

function fmtDate(d) {
  if (!d) return null;
  try {
    return format(parseISO(d), 'd MMM', { locale: it });
  } catch {
    return d;
  }
}

function initials(name) {
  if (!name) return null;
  return name.split(/\s+/).map((w) => w[0]).slice(0, 2).join('').toUpperCase();
}

function pickNextStep(project) {
  const candidates = [];
  if (project.next_action) {
    candidates.push({
      label: project.next_action,
      who: project.next_action_assignee,
      when: project.next_action_due,
    });
  }
  if (project.next_shoot_date) {
    candidates.push({
      label: project.next_shoot_luogo ? `Attività a ${project.next_shoot_luogo}` : 'Attività pianificata',
      who: project.next_shoot_staff?.length ? project.next_shoot_staff.join(', ') : null,
      when: project.next_shoot_date,
    });
  }
  if (candidates.length === 0) return null;
  candidates.sort((a, b) => (a.when || '9999').localeCompare(b.when || '9999'));
  return candidates[0];
}

// ---------------------------------------------------------------------------
// Card commessa: nome, barra di avanzamento sul processo UFFICIALE (ian-processi),
// step corrente con i referenti del documento, prossima task, bottone Avanza
// (solo manager). Niente drag: avanzare = un click.
// ---------------------------------------------------------------------------
const CommessaCard = ({ project, processes, canMove, onOpen, onAdvance, onSetType }) => {
  const proc = project.process_type ? processes[project.process_type] : null;
  const total = proc ? proc.steps.length : 0;
  const stepN = project.process_step || 0;
  const step = proc && stepN >= 1 ? proc.steps[stepN - 1] : null;
  const isDone = proc && stepN === total;
  const nextStep = pickNextStep(project);
  const who = nextStep?.who || null;

  return (
    <div
      onClick={() => onOpen(project)}
      title={project.is_blocked ? `Bloccata: ${project.blocked_manual_reason || 'dipendenza non risolta'}` : undefined}
      className="cursor-pointer rounded-xl bg-white p-3.5 shadow-[0_1px_3px_rgba(0,0,0,0.12)] transition hover:shadow-md"
    >
      {(project.is_blocked || project.is_overdue) && (
        <div className="mb-2 flex gap-1">
          {project.is_blocked && <span className="h-1.5 w-10 rounded-full bg-red-500" />}
          {project.is_overdue && <span className="h-1.5 w-10 rounded-full bg-amber-400" />}
        </div>
      )}

      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="text-sm font-semibold leading-snug text-slate-800">{project.name}</p>
          {project.client && project.client !== project.name && (
            <p className="truncate text-xs text-slate-400">{project.client}</p>
          )}
        </div>
        {who && (
          <span
            title={who}
            className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-slate-200 text-[10px] font-semibold text-slate-600"
          >
            {initials(who)}
          </span>
        )}
      </div>

      {proc ? (
        <div className="mt-3">
          <div className="h-1.5 w-full overflow-hidden rounded-full bg-slate-100">
            <div
              className={`h-full rounded-full ${isDone ? 'bg-emerald-500' : 'bg-sky-500'}`}
              style={{ width: `${Math.round((stepN / total) * 100)}%` }}
            />
          </div>
          <p className="mt-1.5 text-xs text-slate-600">
            <span className="font-semibold">{stepN}/{total}</span>
            {step && <> · {step.name}</>}
          </p>
          {step && step.referenti.length > 0 && (
            <p className="truncate text-xs text-slate-400">→ {step.referenti.join(', ')}</p>
          )}
        </div>
      ) : (
        <div className="mt-3" onClick={(e) => e.stopPropagation()}>
          {canMove ? (
            <div className="flex items-center gap-1.5">
              <span className="text-xs text-slate-400">Processo:</span>
              <button
                onClick={() => onSetType(project, 'video')}
                className="rounded-full border border-slate-200 px-2 py-0.5 text-xs text-slate-500 hover:border-sky-400 hover:text-sky-600"
              >
                Video
              </button>
              <button
                onClick={() => onSetType(project, 'social')}
                className="rounded-full border border-slate-200 px-2 py-0.5 text-xs text-slate-500 hover:border-sky-400 hover:text-sky-600"
              >
                Social
              </button>
            </div>
          ) : (
            <p className="text-xs italic text-slate-300">Processo da impostare</p>
          )}
        </div>
      )}

      {(nextStep || project.is_blocked) && (
        <div className="mt-2 flex items-center justify-between border-t pt-2">
          <p className="truncate text-xs text-slate-500">
            {project.is_blocked
              ? <span className="font-medium text-red-600">{project.blocked_manual_reason || 'Bloccata'}</span>
              : nextStep.label}
          </p>
          {!project.is_blocked && nextStep?.when && (
            <span
              className={`ml-2 inline-flex shrink-0 items-center gap-1 rounded px-1.5 py-0.5 text-[11px] ${
                project.is_overdue ? 'bg-amber-100 font-medium text-amber-700' : 'bg-slate-100 text-slate-500'
              }`}
            >
              <Clock className="h-3 w-3" /> {fmtDate(nextStep.when)}
            </span>
          )}
        </div>
      )}

      {canMove && proc && stepN < total && (
        <div className="mt-2.5" onClick={(e) => e.stopPropagation()}>
          <Button size="sm" variant="outline" className="h-7 w-full text-xs" onClick={() => onAdvance(project)}>
            Avanza <ChevronRight className="ml-1 h-3 w-3" />
          </Button>
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Un'unica lista Task per progetto: native (modificabili) + Notion (sola lettura).
// ---------------------------------------------------------------------------
const TasksSection = ({ project, members, userId, notionTasks, notionTasksLoading }) => {
  const { shoots, loading, createShoot, updateShoot, deleteShoot } = useOperationsShoots(project?.notion_page_id);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: '', date_start: '', luogo: '', staff_user_ids: [] });

  const resetForm = () => setForm({ name: '', date_start: '', luogo: '', staff_user_ids: [] });

  const rows = useMemo(() => {
    const native = (shoots || []).map((s) => ({
      id: `n-${s.id}`, rawId: s.id, native: true,
      name: s.name,
      who: s.staff_display_names?.length ? s.staff_display_names.join(', ') : null,
      when: s.date_start, extra: s.luogo,
      status: s.notion_status,
      done: s.notion_status === 'Fatto',
    }));
    const synced = (notionTasks || []).map((t) => ({
      id: `t-${t.id}`, native: false,
      name: t.name,
      who: t.notion_assignee_name || null,
      when: t.local_due_date_override || t.due_date, extra: null,
      status: t.notion_status,
      done: t.notion_status === 'Fatto' || t.notion_status === 'Archiviato',
    }));
    return [...native, ...synced].sort((a, b) => {
      if (a.done !== b.done) return a.done ? 1 : -1;
      return (a.when || '9999').localeCompare(b.when || '9999');
    });
  }, [shoots, notionTasks]);

  const toggleStaff = (id) => {
    setForm((f) => ({
      ...f,
      staff_user_ids: f.staff_user_ids.includes(id)
        ? f.staff_user_ids.filter((x) => x !== id)
        : [...f.staff_user_ids, id],
    }));
  };

  const submit = async () => {
    if (!form.name.trim()) {
      toast.error('Serve un nome');
      return;
    }
    const res = await createShoot({
      name: form.name.trim(),
      date_start: form.date_start ? new Date(form.date_start).toISOString() : null,
      is_datetime: true,
      luogo: form.luogo || null,
      staff_user_ids: form.staff_user_ids,
      project_notion_page_id: project.notion_page_id,
      created_by: userId,
    });
    if (res.success) {
      toast.success('Attività creata');
      setShowForm(false);
    } else {
      toast.error(res.error || 'Errore creazione attività');
    }
  };

  return (
    <div>
      <div className="mb-2 flex items-center justify-between">
        <p className="text-xs uppercase text-slate-400">Task</p>
        <Button size="sm" variant="ghost" onClick={() => { resetForm(); setShowForm((v) => !v); }}>
          <Plus className="mr-1 h-3 w-3" /> Nuova
        </Button>
      </div>

      {showForm && (
        <div className="mb-3 space-y-2 rounded border p-3">
          <Input placeholder="Es: Shooting, Montaggio, Script..." value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          <Input
            type="datetime-local"
            value={form.date_start}
            onChange={(e) => setForm({ ...form, date_start: e.target.value })}
          />
          <Input placeholder="Luogo (opzionale)" value={form.luogo} onChange={(e) => setForm({ ...form, luogo: e.target.value })} />
          <div>
            <p className="mb-1 text-xs text-slate-400">Staff</p>
            <div className="flex flex-wrap gap-3">
              {members.map((m) => (
                <label key={m.id} className="flex items-center gap-1 text-xs">
                  <Checkbox
                    checked={form.staff_user_ids.includes(m.id)}
                    onCheckedChange={() => toggleStaff(m.id)}
                  />
                  {m.name}
                </label>
              ))}
            </div>
          </div>
          <Button size="sm" onClick={submit}>Salva</Button>
        </div>
      )}

      {(loading || notionTasksLoading) ? (
        <p className="text-slate-400">Caricamento...</p>
      ) : rows.length === 0 ? (
        <p className="text-slate-400">Nessuna task.</p>
      ) : (
        <ul className="space-y-1.5">
          {rows.map((r) => (
            <li
              key={r.id}
              className={`flex items-start justify-between gap-2 rounded-md border p-2 ${r.done ? 'opacity-50' : ''}`}
            >
              <div className="min-w-0">
                <p className={`text-sm font-medium ${r.done ? 'line-through' : ''}`}>{r.name}</p>
                <p className="truncate text-xs text-slate-500">
                  {[r.who, fmtDate(r.when), r.extra, r.native ? null : r.status]
                    .filter(Boolean).join(' · ') || '—'}
                </p>
              </div>
              <div className="flex shrink-0 items-center gap-1">
                {r.native ? (
                  <>
                    {!r.done && (
                      <Button size="sm" variant="ghost" onClick={() => updateShoot(r.rawId, { notion_status: 'Fatto' })}>
                        Fatto
                      </Button>
                    )}
                    <Button size="sm" variant="ghost" onClick={() => deleteShoot(r.rawId)}>
                      <Trash2 className="h-3 w-3 text-red-500" />
                    </Button>
                  </>
                ) : (
                  <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[10px] text-slate-400">Notion</span>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

const ProjectDetailSheet = ({ project, open, onClose, onSetBlocked, onArchive, onUpdateProcess, processes, canMove, members, userId }) => {
  const { tasks, loading: tasksLoading } = useOperationsTasks(open ? project?.notion_page_id : null);
  const [reason, setReason] = useState(project?.blocked_manual_reason || '');
  const [showMore, setShowMore] = useState(false);

  useEffect(() => {
    setReason(project?.blocked_manual_reason || '');
    setShowMore(false);
  }, [project]);

  if (!project) return null;

  const proc = project.process_type ? processes[project.process_type] : null;

  return (
    <Sheet open={open} onOpenChange={(v) => !v && onClose()}>
      <SheetContent className="w-full overflow-y-auto sm:max-w-md">
        <SheetHeader>
          <SheetTitle>{project.name}</SheetTitle>
          <SheetDescription>{project.client !== project.name ? project.client : null}</SheetDescription>
        </SheetHeader>

        <div className="mt-4 space-y-5 text-sm">
          {canMove ? (
            <div className="flex items-center gap-2">
              <select
                value={project.process_type || ''}
                onChange={(e) => onUpdateProcess(project, { process_type: e.target.value || null, process_step: e.target.value ? 1 : null })}
                className="rounded border border-slate-200 bg-white px-2 py-1 text-xs text-slate-600"
              >
                <option value="">Processo…</option>
                <option value="video">Produzione video</option>
                <option value="social">Gestione social</option>
              </select>
              {proc && (
                <select
                  value={project.process_step || 1}
                  onChange={(e) => onUpdateProcess(project, { process_step: Number(e.target.value) })}
                  className="min-w-0 flex-1 rounded border border-slate-200 bg-white px-2 py-1 text-xs text-slate-600"
                >
                  {proc.steps.map((s) => (
                    <option key={s.n} value={s.n}>{s.n}. {s.name}</option>
                  ))}
                </select>
              )}
            </div>
          ) : (
            proc && project.process_step && (
              <p className="text-xs text-slate-500">
                {proc.label} · {project.process_step}/{proc.steps.length} · {proc.steps[project.process_step - 1]?.name}
              </p>
            )
          )}

          {project.blocked_manual_reason && (
            <p className="text-xs font-medium text-red-600">Bloccata: {project.blocked_manual_reason}</p>
          )}
          {project.summary && project.summary !== 'No content' && (
            <p className="text-slate-600">{project.summary}</p>
          )}

          <TasksSection
            project={project}
            members={members}
            userId={userId}
            notionTasks={tasks}
            notionTasksLoading={tasksLoading}
          />

          {/* Azioni rare, fuori dalla vista principale. */}
          <div className="border-t pt-3">
            <button
              type="button"
              onClick={() => setShowMore((v) => !v)}
              className="text-xs text-slate-400 hover:text-slate-600"
            >
              {showMore ? 'Altro ▴' : 'Altro ▾'}
            </button>
            {showMore && (
              <div className="mt-3 space-y-3">
                <div>
                  <Label className="text-xs uppercase text-slate-400">Blocco manuale</Label>
                  <Textarea
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    placeholder="es: sopralluogo non confermato dal cliente"
                    className="mt-1"
                  />
                  <div className="mt-2 flex gap-2">
                    <Button size="sm" onClick={() => onSetBlocked(project.id, reason)}>Salva blocco</Button>
                    {project.blocked_manual_reason && (
                      <Button size="sm" variant="outline" onClick={() => { setReason(''); onSetBlocked(project.id, ''); }}>
                        Rimuovi blocco
                      </Button>
                    )}
                  </div>
                </div>
                <Button size="sm" variant="outline" onClick={() => onArchive(project.id)}>
                  Archivia (nascondi dal board)
                </Button>
              </div>
            )}
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
};

// ---------------------------------------------------------------------------
// Tab Calendario: la vista task che il team già conosceva da dondoit
// (ScheduleCalendar: backlog + mese con drag-and-drop), alimentata dalle task
// Operations tramite un adapter sottile.
// ---------------------------------------------------------------------------
const STATUS_MAP = {
  'Fatto': 'completed',
  'In progress': 'in_progress',
  'Held On Client/Task': 'waiting',
};

const CalendarTab = ({ members, user }) => {
  const { shoots, createShoot, updateShoot } = useOperationsShoots();

  const calTasks = useMemo(() => (shoots || []).map((s) => ({
    id: s.id,
    title: s.name,
    assigned_to_name: s.staff_display_names?.length ? s.staff_display_names.join(', ') : '—',
    due_date: s.date_start ? s.date_start.slice(0, 10) : null,
    status: STATUS_MAP[s.notion_status] || 'pending',
  })), [shoots]);

  return (
    <div className="rounded-xl bg-zinc-950 p-4">
      <ScheduleCalendar
        tasks={calTasks}
        members={members}
        user={user}
        apiUpdateTask={(id, patch) => updateShoot(id, { date_start: patch.due_date ?? null })}
        createTask={(t) => createShoot({
          name: t.title,
          staff_user_ids: t.assigned_to_ids || [],
          date_start: t.due_date || null,
          is_datetime: false,
          project_notion_page_id: null,
          created_by: user.id,
        })}
      />
    </div>
  );
};

export default function OperationsBoard() {
  const { user, logout, isManager, isAdmin } = useAuth();
  const navigate = useNavigate();
  // Dalla weekly: l'unico che fa avanzare i clienti nel processo è Andrea.
  const canMove = isManager || isAdmin;
  const { projects, loading, syncing, syncStatus, syncNow, updateProcess, setBlocked, archiveProject } = useOperationsProjects();
  const [members, setMembers] = useState([]);
  const [processes, setProcesses] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [tab, setTab] = useState('commesse'); // 'commesse' | 'calendario'
  // Derivato da projects così il pannello riflette subito ogni aggiornamento.
  const selectedProject = useMemo(
    () => projects.find((p) => p.id === selectedId) || null,
    [projects, selectedId]
  );

  useEffect(() => {
    axios.get(`${API}/users/members`).then((r) => setMembers(Array.isArray(r.data) ? r.data : [])).catch(() => {});
    axios.get(`${API}/operations/processes`).then((r) => setProcesses(r.data)).catch(() => {});
  }, []);

  const groups = useMemo(() => {
    const g = { video: [], social: [], unset: [] };
    projects.forEach((p) => {
      if (p.process_type === 'video') g.video.push(p);
      else if (p.process_type === 'social') g.social.push(p);
      else g.unset.push(p);
    });
    const urgency = (p) => {
      const proc = p.process_type && processes ? processes[p.process_type] : null;
      const done = proc && p.process_step === proc.steps.length;
      const next = pickNextStep(p);
      return [done ? 1 : 0, p.is_blocked ? 0 : 1, p.is_overdue ? 0 : 1, next?.when || '9999'];
    };
    Object.values(g).forEach((list) =>
      list.sort((a, b) => {
        const ua = urgency(a); const ub = urgency(b);
        for (let i = 0; i < ua.length; i++) {
          if (ua[i] < ub[i]) return -1;
          if (ua[i] > ub[i]) return 1;
        }
        return 0;
      })
    );
    return g;
  }, [projects, processes]);

  const handleAdvance = async (project) => {
    const proc = processes[project.process_type];
    const next = (project.process_step || 0) + 1;
    const res = await updateProcess(project.id, { process_step: next }, user.id);
    if (!res.success) {
      toast.error(res.error || 'Errore avanzamento');
      return;
    }
    const step = proc.steps[next - 1];
    const refs = (step.referenti || []).join(', ');
    toast.success(`${project.name} → ${next}/${proc.steps.length} · ${step.name}`, refs ? {
      action: {
        label: `Avvisa ${refs}`,
        onClick: () => updateProcess(project.id, { process_step: next, notify: true }, user.id)
          .then((r) => r.success && toast.success(r.notified?.length ? `Avvisati: ${r.notified.join(', ')}` : 'Nessun account da avvisare per questo step')),
      },
    } : undefined);
  };

  const handleSetType = (project, type) => {
    updateProcess(project.id, { process_type: type, process_step: 1 }, user.id)
      .then((r) => { if (!r.success) toast.error(r.error || 'Errore'); });
  };

  const handleSync = async () => {
    const res = await syncNow(user?.id);
    if (res?.success) {
      toast.success(`Sync completata: ${res.projects_upserted} commesse, ${res.tasks_upserted} task`);
    } else {
      toast.error(res?.error || 'Sync fallita');
    }
  };

  const renderGroup = (title, list) => {
    if (!list.length) return null;
    return (
      <div key={title}>
        <p className="mb-2 text-sm font-semibold text-slate-600">
          {title} <span className="font-normal text-slate-400">({list.length})</span>
        </p>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {list.map((p) => (
            <CommessaCard
              key={p.id}
              project={p}
              processes={processes}
              canMove={canMove}
              onOpen={(p) => setSelectedId(p.id)}
              onAdvance={handleAdvance}
              onSetType={handleSetType}
            />
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-slate-100">
      <header className="flex items-center justify-between border-b bg-white px-6 py-3">
        <div className="flex items-center gap-6">
          <div>
            <h1 className="text-lg font-semibold text-slate-900">Operations</h1>
            <p className="text-xs text-slate-400">
              {syncStatus?.finished_at
                ? `Ultimo sync: ${format(parseISO(syncStatus.finished_at), "d MMM HH:mm", { locale: it })}`
                : 'Nessun sync ancora eseguito'}
            </p>
          </div>
          <nav className="flex gap-1">
            {[['commesse', 'Commesse'], ['calendario', 'Calendario']].map(([key, label]) => (
              <button
                key={key}
                onClick={() => setTab(key)}
                className={`rounded-md px-3 py-1.5 text-sm ${
                  tab === key ? 'bg-slate-900 font-medium text-white' : 'text-slate-500 hover:bg-slate-100'
                }`}
              >
                {label}
              </button>
            ))}
          </nav>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={handleSync} disabled={syncing}>
            <RefreshCw className={`mr-2 h-4 w-4 ${syncing ? 'animate-spin' : ''}`} /> Sync Notion
          </Button>
          <Button variant="ghost" size="sm" onClick={() => navigate('/manager')}>Don Doit</Button>
          <Button variant="ghost" size="sm" onClick={logout}><LogOut className="h-4 w-4" /></Button>
        </div>
      </header>

      <main className="p-5">
        {tab === 'calendario' ? (
          <CalendarTab members={members} user={user} />
        ) : (loading || !processes) ? (
          <div className="p-8 text-center text-slate-400">Caricamento commesse...</div>
        ) : (
          <div className="space-y-7">
            {renderGroup('Produzione video', groups.video)}
            {renderGroup('Gestione social', groups.social)}
            {renderGroup('Da impostare', groups.unset)}
          </div>
        )}
      </main>

      <ProjectDetailSheet
        project={selectedProject}
        open={!!selectedProject}
        onClose={() => setSelectedId(null)}
        onSetBlocked={(id, reason) => setBlocked(id, reason, user.id).then((r) => {
          if (r.success) toast.success(reason ? 'Blocco salvato' : 'Blocco rimosso');
        })}
        onArchive={(id) => archiveProject(id, true, user.id).then((r) => {
          if (r.success) {
            toast.success('Commessa archiviata');
            setSelectedId(null);
          }
        })}
        onUpdateProcess={(project, patch) => updateProcess(project.id, patch, user.id)
          .then((r) => { if (!r.success) toast.error(r.error || 'Errore'); })}
        processes={processes || {}}
        canMove={canMove}
        members={members}
        userId={user?.id}
      />
    </div>
  );
}
